import { Config } from "@config";
import type { FunctionComponent, ReactNode } from "react";
import React from "react";
import { FeatureFlagConfig } from "../../utils/FeatureFlagConfig";

type FeatureFlagProps = {
	/**
	 * The feature flag to check.
	 */
	flag: string;
	/**
	 * Whether the feature flag should be disabled instead of enabled.
	 */
	disabled?: boolean;
	/**
	 * The child components to be rendered depending on the feature flag.
	 */
	children: ReactNode;
};

/**
 * A wrapper component that conditionally renders its children based on a feature flag.
 *
 * @example
 * // Render if the feature flag is enabled.
 * <FeatureFlag flag="new-feature">
 *    <NewFeatureComponent />
 * </FeatureFlag>
 *
 * // Render if the feature flag is not enabled.
 * <FeatureFlag flag="new-feature" disabled>
 *    <NewFeatureComponent />
 * </FeatureFlag>
 */
export const FeatureFlag: FunctionComponent<FeatureFlagProps> = (props) => {
	const { children, flag, disabled = false } = props;

	if (Config.isSsr()) {
		const openTag = `##FEATURE-FLAG:IF${disabled ? "-NOT" : ""}:${flag}:START##`;
		return (
			<>
				{openTag}
				{children}
				##FEATURE-FLAG:END##
			</>
		);
	}

	if (FeatureFlagConfig.isEnabled(flag) !== disabled) {
		return <>{children}</>;
	}

	return null;
};
